import * as React from 'react';
import { components, DropdownIndicatorProps, ClearIndicatorProps, GroupBase, StylesConfig } from 'react-select';

// react-select 5 styled to look like react-select 1 (and its react-select.css), which the forms were designed with

export type Option = { value: string; label: string };

const blue = '#007eff';

// the css triangle react-select 1 used, pointing up while the menu is open
const arrowStyle = (isOpen: boolean): React.CSSProperties => ({
  borderColor: isOpen ? 'transparent transparent #999' : '#999 transparent transparent',
  borderStyle: 'solid',
  borderWidth: isOpen ? '0 5px 5px' : '5px 5px 2.5px',
  display: 'inline-block',
  height: 0,
  width: 0,
  position: 'relative',
  top: isOpen ? -2 : 0,
});

export const DropdownIndicator = (props: DropdownIndicatorProps<Option, false, GroupBase<Option>>) => (
  <components.DropdownIndicator {...props}>
    <span style={arrowStyle(props.selectProps.menuIsOpen)} />
  </components.DropdownIndicator>
);

export const ClearIndicator = (props: ClearIndicatorProps<Option, false, GroupBase<Option>>) => (
  <components.ClearIndicator {...props}>
    <span style={{ display: 'inline-block', fontSize: 18, lineHeight: 1 }}>×</span>
  </components.ClearIndicator>
);

export const selectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#fff',
    // longhands, since a `border` shorthand would come after react-select's own borderColor and override it
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: state.isFocused && !state.menuIsOpen ? blue : state.menuIsOpen ? '#b3b3b3 #ccc #d9d9d9' : '#ccc',
    borderRadius: 4,
    borderBottomLeftRadius: state.menuIsOpen ? 0 : 4,
    borderBottomRightRadius: state.menuIsOpen ? 0 : 4,
    boxShadow:
      state.isFocused && !state.menuIsOpen
        ? 'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(0, 126, 255, 0.1)'
        : 'none',
    color: '#333',
    cursor: 'default',
    minHeight: 36,
    height: 36,
    '&:hover': {
      borderColor: state.isFocused && !state.menuIsOpen ? blue : state.menuIsOpen ? '#b3b3b3 #ccc #d9d9d9' : '#ccc',
      boxShadow:
        state.isFocused && !state.menuIsOpen
          ? 'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(0, 126, 255, 0.1)'
          : '0 1px 0 rgba(0, 0, 0, 0.06)',
    },
  }),
  valueContainer: base => ({ ...base, padding: '0 10px', height: 34 }),
  placeholder: base => ({ ...base, color: '#aaa', margin: 0 }),
  singleValue: base => ({ ...base, color: '#333', margin: 0 }),
  input: base => ({ ...base, margin: 0, padding: 0, color: '#333' }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: base => ({ ...base, height: 34, paddingRight: 5 }),
  clearIndicator: base => ({
    ...base,
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: 17,
    justifyContent: 'center',
    '&:hover': { color: '#D0021B' },
  }),
  dropdownIndicator: base => ({
    ...base,
    cursor: 'pointer',
    padding: 0,
    width: 25,
    justifyContent: 'center',
  }),
  menu: base => ({
    ...base,
    marginTop: -1,
    marginBottom: 0,
    borderRadius: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderTopColor: '#e6e6e6',
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
    zIndex: 1,
  }),
  menuList: base => ({ ...base, maxHeight: 198, padding: 0 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(0, 126, 255, 0.08)'
      : state.isSelected
        ? 'rgba(0, 126, 255, 0.04)'
        : '#fff',
    color: state.isFocused || state.isSelected ? '#333' : '#666666',
    cursor: 'pointer',
    padding: '8px 10px',
    '&:active': { backgroundColor: 'rgba(0, 126, 255, 0.08)' },
  }),
  noOptionsMessage: base => ({ ...base, color: '#999999', padding: '8px 10px', textAlign: 'left' }),
};
